import { useEffect, useRef } from 'react';

/**
 * Small anchored confirmation (place inside a `relative` parent). Escape or a
 * click outside cancels; focus starts on Cancel.
 */
export function Confirm({
  title,
  body,
  confirmLabel,
  onConfirm,
  onCancel,
  busy,
  className = 'right-0 top-full mt-1',
}: {
  title: string;
  body?: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  busy?: boolean;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.querySelector<HTMLButtonElement>('[data-cancel]')?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onCancel();
    const onDown = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && onCancel();
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [onCancel]);

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={title}
      className={`absolute z-30 w-64 rounded-xl border border-line bg-surface p-3 text-left shadow-lg ${className}`}
    >
      <p className="text-sm font-semibold text-ink">{title}</p>
      {body && <p className="mt-1 text-xs text-ink-soft">{body}</p>}
      <div className="mt-3 flex justify-end gap-2">
        <button
          data-cancel
          type="button"
          onClick={onCancel}
          className="rounded-lg px-3 py-1.5 text-xs font-semibold text-ink-soft hover:bg-surface-sunken"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={busy}
          className="rounded-lg bg-ink px-3 py-1.5 text-xs font-semibold text-white hover:bg-ink-strong disabled:opacity-50"
        >
          {confirmLabel}
        </button>
      </div>
    </div>
  );
}
